import { useForm, FormProvider } from "react-hook-form";
import { classes } from "../../../../../util/style-utils";
import useYesNoOptions from "../../../../../hooks/useYesNoOptions";
import { useContext } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import GeaoVerticalStepperLayout from "../../shared/Layout/GeaoVerticalStepperLayout";
import PrincipalFlowVerticalStepperElements from "../../../constants/PrincipalFlowVerticalStepperElements";
import { GeaoContext } from '../../../GeaoContextProvider';
import useTitle from '../../../../../hooks/useTitle';
import Input from "../../../../gds/Input";
import Ssn from "../../../../gdk/Ssn";
import AgencyAddresses from "./AgencyAddresses";
import Fein from "../../../../gdk/Fein";
import CentersOfOperation from "./CentersOfOperation";
import RadioButtonGroup from "../../../../gds/RadioButtonGroup";
import AgencyAffiliations from "./AgencyAffiliations";
import CobrandedCommissionsAcknowledgement from "./CobrandedCommissionsAcknowledgement";
import useFormEvents from "./useFormEvents";
import useMappers from "./useMappers";
import useValidationSchema from "./useValidationSchema";
import useCobrandedAgency from "../../../hooks/useCobrandedAgency";
import SelectBox from "../../../../gds/SelectBox";
import { EntityTypeOptions } from "./EntityTypeOptions";
import { PartnershipTypeOptions } from "./PartnershipTypeOptions";
import { UsStateOptions } from "../../../../../constants/options/UsStateOptions";
import { scrollToFirstError } from "../../../../../util/form-utils";

export default function PrincipalAgencyOfficeInformation() {
    useTitle("Office Information");

    const {
        agencyInterview,
        agentInterview
    } = useContext(GeaoContext);

    const { isCobrandedAgency } = useCobrandedAgency();

    const { defaultValues } = useMappers();
    const validationSchema = useValidationSchema();

    const useFormReturn = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues
    });

    const { register, handleSubmit, setFocus, formState: { errors }, watch, control } = useFormReturn;

    const onInvalid = (formErrors) => {
        scrollToFirstError(formErrors, setFocus);
    };

    const interviewIsCompleted = agentInterview?.outcome === "Complete";

    const yesNoOptions = useYesNoOptions();

    const watchFeinOrSsn = watch("feinOrSsn");
    const watchw9RegistrationAsIndividual = watch("w9RegistrationAsIndividual");
    const watchFEIN = watch("fein");
    const watchEntityType = watch("entityType");

    const {
        onSaveAndExit,
        onSubmit,
        saving
    } = useFormEvents(useFormReturn?.trigger, useFormReturn?.getValues);

    return (
        <GeaoVerticalStepperLayout
            verticalStepperElements={PrincipalFlowVerticalStepperElements}
            currentStepperItem="Office Information"
            header="Office Information"
        >
            <FormProvider {...useFormReturn}>
                <form className="form geao-form" onSubmit={handleSubmit(onSubmit, onInvalid)}>
                    <h3 className="margin-top margin-bottom">{agencyInterview?.questionAnswers?.AgencyName?.answer ?? "Agency"} Information</h3>

                    <div className="flex-row label-row">
                        <div className="form-field">
                            <label htmlFor="dba" className="text">
                                Agency Doing Business As (DBA), If Applicable
                            </label>
                        </div>

                        {(watchw9RegistrationAsIndividual === "false" || watchFEIN !== "") &&
                            <div className="form-field">
                                <label htmlFor="agency-npn" className="text">
                                    Agency National Producer Number (NPN){/*
                                    */}<span className='required'>*</span>
                                </label>
                            </div>
                        }
                    </div>

                    <div className="flex-row">
                        <div className={classes("form-field", errors.dba && "form-field--error")}>
                            <input id='dba' type="text" {...register("dba")} />
                            {errors.dba &&
                                <span className="form-message">{errors.dba.message}</span>
                            }
                        </div>
                        {(watchw9RegistrationAsIndividual === "false" || watchFEIN !== "") &&
                            <Input
                                input-id="agency-npn"
                                mask="000000000"
                                full-width
                                {...register("agencyNpn")}
                            >
                                {errors.agencyNpn &&
                                    <span slot="error-message-copy">{errors.agencyNpn.message}</span>
                                }
                            </Input>
                        }
                    </div>

                    <div className="flex-row">
                        <div className={classes("form-field", errors.yearsInBusiness && "form-field--error")}>
                            <label htmlFor="years-in-business" className="text">
                                Years in Business{/*
                            */}<span className='required'>*</span>
                            </label>
                            <input id="years-in-business" type="text" {...register("yearsInBusiness")} />
                            {errors.yearsInBusiness &&
                                <span className="form-message">{errors.yearsInBusiness.message}</span>
                            }
                        </div>
                    </div>

                    {/* Sole Prop questions - Hidden for cobranded agencies (defaulted to No/FEIN) */}
                    {!isCobrandedAgency && (
                        <>
                            <RadioButtonGroup
                                control={control}
                                name="w9RegistrationAsIndividual"
                                legend={<>
                                    Are you registered as an individual/sole proprietor or single-member LLC?{/*
                                    */}<span className='required'>*</span>
                                </>}
                                layout="Two Column"
                                options={yesNoOptions}
                            />

                            <RadioButtonGroup
                                control={control}
                                name="feinOrSsn"
                                legend={<>
                                    Do you report a Federal Employer Identification Number (FEIN) or SSN on your W-9?{/*
                                    */}<span className='required'>*</span>
                                </>}
                                layout="One Column"
                                options={feinOrSsnOptions}
                            />
                        </>
                    )}

                    {watchFeinOrSsn === "fein" &&
                        <div className="flex-row">
                            <Fein
                                control={control}
                                name="fein"
                                label={<>
                                    Federal Employer Identification Number (FEIN){/*
                                    */}<span className='required'>*</span>
                                </>}
                                disabled={!!agencyInterview?.questionAnswers?.Fein?.answer}
                            />
                        </div>
                    }

                    {watchFeinOrSsn === "ssn" &&
                        <div className="flex-row">
                            <Ssn
                                control={control}
                                name="ssn"
                                label={<>
                                    SSN{/*
                                    */}<span className='required'>*</span>
                                </>}
                            />
                        </div>
                    }

                    {isCobrandedAgency &&
                        <>
                            <div className="flex-row">
                                <SelectBox
                                    control={control}
                                    name="entityType"
                                    label={<>
                                        Type of Entity{/*
                                        */}<span className='required'>*</span>
                                    </>}
                                    options={EntityTypeOptions}
                                    placeholder="Please Select"
                                />

                                {watchEntityType === "Partnership" &&
                                    <SelectBox
                                        control={control}
                                        name="partnershipType"
                                        label={<>
                                            Partnership Type{/*
                                            */}<span className='required'>*</span>
                                        </>}
                                        options={PartnershipTypeOptions}
                                        placeholder="Please Select"
                                    />
                                }
                            </div>

                            <div className="flex-row">
                                <SelectBox
                                    control={control}
                                    name="entityFiledState"
                                    label={<>
                                        State where Entity was filed{/*
                                        */}<span className='required'>*</span>
                                    </>}
                                    options={UsStateOptions}
                                    placeholder="Please Select"
                                />
                            </div>
                        </>
                    }

                    <AgencyAddresses />

                    <AgencyAffiliations />

                    <CentersOfOperation />

                    <CobrandedCommissionsAcknowledgement />

                    <div className="flex-row">
                        <p>
                            <span className='required'>*</span> Indicates required field.
                        </p>
                    </div>

                    <div className="margin-top">
                        <button
                            type="submit"
                            className="btn btn--primary btn--full-mobile btn--pull-right"
                            disabled={saving || interviewIsCompleted}
                        >
                            <span>Next</span>
                        </button>

                        <button
                            type="button"
                            className="btn btn--secondary btn--full-mobile btn--pull-right"
                            disabled={saving || interviewIsCompleted}
                            onClick={onSaveAndExit}
                        >
                            <span>Save And Exit</span>
                        </button>
                    </div>
                </form>
            </FormProvider>
        </GeaoVerticalStepperLayout >
    )
}

const feinOrSsnOptions = [
    { value: "fein", label: "Federal Employer Identification Number (FEIN)" },
    { value: "ssn", label: "SSN" }
]